import { STOCK_LEVEL_STATUS } from 'pages/ordering/constants'

import moment from 'moment'

import ProjectOrderPresenter from 'pages/ordering/ProjectOrderPresenter/projectOrderPresenter'
import { HardwareSupplierFilterKeyType, StockLevelStatus, StockLevelType } from 'pages/ordering/type'

import { orgSelectors } from 'ducks/orgs'
import { numberWithCommas } from 'util/misc'
import { DistributorDataTypeV2 } from '../types'

export type TwoPartStockMessageType = { title: string; subtitle: string | null }

export type StockStatusMessagesType = {
  status: StockLevelStatus
  componentSelectorInfoText?: string
  partsListMessage?: TwoPartStockMessageType | null
  productTileStockMessage: TwoPartStockMessageType
  productDetailsStockMessage: TwoPartStockMessageType
  orderValue: number
  distributor: HardwareSupplierFilterKeyType
  unitPrice: number
  quantity: number
  scenario: string
}

export const STOCK_LEVEL_SCENARIO = {
  IN_STOCK: 'in_stock',
  OUT_OF_STOCK: 'out_of_stock',
  IN_STOCK_WITH_INCOMING_SCHEDULE: 'in_stock_with_incoming_schedule',
  OUT_OF_STOCK_WITH_INCOMING_SCHEDULE: 'out_of_stock_with_incoming_schedule',
  PARTIALLY_IN_STOCK: 'partially_in_stock',
  MERELY_IN_STOCK: 'merely_in_stock',
  STOCK_AVAILABILITY_UPON_REQUEST: 'stock_availability_upon_request',
}

export const AVAILABLE_STATUSES = [
  STOCK_LEVEL_STATUS.AVAILABLE,
  STOCK_LEVEL_STATUS.AVAILABLE_FOR_PRE_ORDER,
  STOCK_LEVEL_STATUS.LOW_STOCK,
  STOCK_LEVEL_STATUS.PARTIAL_AVAILABLE,
]

const getIncomingStocksInfo = (incomingStocks: StockLevelType[], includeUnits = false): string => {
  let units = ''
  if (includeUnits) {
    units = ' units'
  }
  const now = moment()

  const futureStocks = incomingStocks
    ?.filter((stock) => moment(stock.planned_date).isAfter(now, 'day'))
    .sort((a, b) => moment(a.planned_date).diff(moment(b.planned_date)))

  const firstStock = futureStocks?.[0]
  if (!firstStock) return ''
  return `${moment(firstStock.planned_date).format('D/M/YYYY')} (${firstStock.quantity} ${units})`
}

/**
 * This function attempts to centralise the logic for the Stock Status messaging, which
 * is distributed throughout the 3.0 code at the moment. It's based on the scenarios doc:
 *
 * https://opensolar.slab.com/posts/stock-status-3-0-wip-q3q5onth
 *
 * The scenarios evaluate under the same conditions eveywhere. We attempt to create every
 * message that will be needed at this point, which is a little inefficient, but puts all
 * the edge cases in one place.
 *
 * There are probably further refactors that can be made here.
 */
export const getStockStatusMessagesPerDistributor = ({
  distributors,
  quantityRequired = 0,
  selectedDistributor = null,
  availableQuantity = 0,
  translate,
}: {
  distributors: DistributorDataTypeV2[] | undefined
  quantityRequired?: number
  selectedDistributor?: DistributorDataTypeV2 | null
  availableQuantity?: number
  translate?: (key: string, options?: any) => string
}): StockStatusMessagesType[] => {
  // Helper function to handle translation with fallback
  const t = (key: string, options?: any): string => {
    if (translate) {
      return translate(key, options)
    }
    // Simple fallback - replace interpolations if needed
    if (options) {
      return key.replace(/%\{(\w+)\}/g, (match, param) => options[param] ?? match)
    }
    return key
  }
  // Mapping distributors data to it corresponding stats. Should return an array of object containing:
  // {
  //   isAvailable: // Check availability for each distributor.
  //   stockLevels: // Accumulating all stock levels for each distributor
  //   quantity: // Mapping of quantity
  // }

  const state = window.reduxStore.getState()
  const enabledDistributorsConfig = orgSelectors.getEnabledSellerIntegrationsConfig(state)
  const enabledDistributorsConfigMap = new Map(
    enabledDistributorsConfig.map((distributor) => [distributor.filterKey, distributor])
  )

  const statsPerDistributor = distributors
    ?.filter((item) => enabledDistributorsConfigMap.has(item.distributor))
    .reduce((accumulator: any, item) => {
      let isAvailable = item.is_available
      let isInStock = item.is_in_stock
      let stockLevels = item.stock_levels
      const preferredStockLocation = enabledDistributorsConfigMap.get(item.distributor)?.preferredStockLocation ?? null
      const preferredStockLevel = item.stock_levels?.find(
        ({ location }) => (location?.key ?? null) === preferredStockLocation && (location?.is_active ?? true)
      )
      const globalStockLevel = item.stock_levels?.find(({ in_stock, location }) => in_stock && location === null)
      let quantity = preferredStockLevel ? preferredStockLevel.quantity : (globalStockLevel?.quantity ?? 0)
      if (accumulator.hasOwnProperty(item.distributor)) {
        isAvailable = accumulator[item.distributor]['isAvailable'] || item.is_available // If any stock level is_available, return true.
        isInStock = accumulator[item.distributor]['isInStock'] || item.is_in_stock
        stockLevels = [...accumulator[item.distributor]['stockLevels'], ...stockLevels]
        quantity = accumulator[item.distributor]['quantity'] + quantity
      }
      accumulator[item.distributor] = { ...accumulator[item.distributor], isAvailable, isInStock, stockLevels, quantity }
      return accumulator
    }, {})

  if (!statsPerDistributor) {
    return []
  }

  // Returns an object containing the stock status of each distributor on each of the different pages.
  const statusPerDistributor = Object.entries(statsPerDistributor).map((item: any) => {
    const [key, value] = item
    let componentSelectorInfoText: string | null = null
    let partsListMessage: TwoPartStockMessageType | null = null
    let productTileStockMessage: TwoPartStockMessageType = {
      title: '',
      subtitle: null,
    }
    let scenario = ''

    let productDetailsStockMessage: TwoPartStockMessageType = {
      title: '',
      subtitle: null,
    }

    let orderValue = 3

    const stockLevels: StockLevelType[] | undefined = value?.stockLevels
      ? Object.values(value?.stockLevels).map((item: any) => {
          const { is_available, ...restItems } = item
          return restItems
        })
      : undefined

    const incomingStocks = stockLevels?.filter((stock) => !stock.in_stock && stock.planned_date)

    const preferredStockLocation = enabledDistributorsConfigMap.get(key)?.preferredStockLocation ?? null

    const status: StockLevelStatus = ProjectOrderPresenter.getStockLevelStatusMainV3({
      distributor: key,
      quantity: quantityRequired,
      isAvailable: value.isAvailable,
      isInStock: value.isInStock,
      stockLevels: stockLevels,
      preferredStockLocation: preferredStockLocation,
    })

    /****************************************************
     * Handle Outlet specific logic
     ****************************************************/
    if (key === 'outlet') {
      scenario = STOCK_LEVEL_SCENARIO.IN_STOCK
      componentSelectorInfoText = ''
      orderValue = 1
      partsListMessage = {
        title: '',
        subtitle: null,
      }
      productTileStockMessage = { title: '', subtitle: null }
      productDetailsStockMessage = { title: '', subtitle: '' }

      if (status === STOCK_LEVEL_STATUS.AVAILABLE) {
        const inStockText = t('In Stock')
        productTileStockMessage = { title: inStockText, subtitle: null }
        partsListMessage = {
          title: '',
          subtitle: null,
        }
        productDetailsStockMessage = { title: inStockText, subtitle: '' }
      } else if (status === STOCK_LEVEL_STATUS.NOT_AVAILABLE) {
        scenario = STOCK_LEVEL_SCENARIO.OUT_OF_STOCK
        const outOfStockText = t('Out of stock')
        productTileStockMessage = { title: outOfStockText, subtitle: null }
        partsListMessage = {
          title: outOfStockText,
          subtitle: null,
        }
        productDetailsStockMessage = { title: outOfStockText, subtitle: '' }
      } else if (status === STOCK_LEVEL_STATUS.AVAILABLE_FOR_PRE_ORDER) {
        scenario = STOCK_LEVEL_SCENARIO.OUT_OF_STOCK_WITH_INCOMING_SCHEDULE
        const preOrderText = t('Pre-order available')
        productTileStockMessage = { title: preOrderText, subtitle: null }
        partsListMessage = {
          title: preOrderText,
          subtitle: null,
        }
        productDetailsStockMessage = { title: preOrderText, subtitle: '' }
      }
    } else {
      // This is very crufty and needs a serious tidy-up.
      // ### Logic for all other distributors
      if (AVAILABLE_STATUSES.includes(status)) {
        // ### Scenario: In stock
        scenario = STOCK_LEVEL_SCENARIO.IN_STOCK
        const inStockText = t('In Stock')
        const quantityInStockText = t('%{count} in stock', { count: numberWithCommas(value.quantity, true) })
        const availableText = t('%{count} available', { count: numberWithCommas(availableQuantity, true) })

        componentSelectorInfoText = inStockText
        productTileStockMessage = { title: quantityInStockText, subtitle: null }

        productDetailsStockMessage = { title: inStockText, subtitle: availableText }

        orderValue = 1

        if (status === STOCK_LEVEL_STATUS.LOW_STOCK) {
          // ### Scenario: Merely In Stock
          scenario = STOCK_LEVEL_SCENARIO.MERELY_IN_STOCK
          const sellingFastText = t('Selling Fast - only %{count} left!', {
            count: numberWithCommas(value.quantity, true),
          })
          partsListMessage = {
            title: sellingFastText,
            subtitle: null,
          }
          productTileStockMessage = {
            title: quantityInStockText,
            subtitle: null,
          }
        } else if (status === STOCK_LEVEL_STATUS.PARTIAL_AVAILABLE) {
          // ### Scenario: Partially In Stock
          scenario = STOCK_LEVEL_SCENARIO.PARTIALLY_IN_STOCK
          const onlyAvailableText = t('Only %{count} available', { count: numberWithCommas(value.quantity, true) })
          partsListMessage = {
            title: onlyAvailableText,
            // subtitle: `${inStockMessageNumber} available`,
            subtitle: null,
          }
          productTileStockMessage = { title: onlyAvailableText, subtitle: null }
          productDetailsStockMessage = {
            title: onlyAvailableText,
            subtitle: availableText,
          }
        }
      } else {
        // ### Scenario: Out of Stock
        scenario = STOCK_LEVEL_SCENARIO.OUT_OF_STOCK
        const outOfStockText = t('Out of stock')
        componentSelectorInfoText = outOfStockText
        partsListMessage = {
          title: outOfStockText,
          subtitle: null,
        }
        productTileStockMessage = { title: outOfStockText, subtitle: null }
        productDetailsStockMessage = {
          title: outOfStockText,
          subtitle: null,
        }

        const preferredStockLocationName =
          stockLevels?.find(({ location }) => location?.key === preferredStockLocation)?.location?.name ?? null
        productDetailsStockMessage = {
          title: 'Out of stock',
          subtitle: preferredStockLocationName ? `Not available in ${preferredStockLocationName}` : null,
        }

        orderValue = 3
      }
    }

    return {
      status,
      componentSelectorInfoText,
      orderValue,
      distributor: key,
      unitPrice: 0, // TODO move these calcs here
      quantity: 0, // TODO move these calcs here
      partsListMessage,
      productTileStockMessage,
      productDetailsStockMessage,
      scenario,
    }
  })
  return statusPerDistributor.sort((a, b) => a.orderValue - b.orderValue)
}
